import React, { useState, useEffect } from 'react';
import { IconButton } from '@material-ui/core';
import { Create as EditIcon } from '@material-ui/icons';
import Table, {
  columnType,
  tempFilteredRows,
} from '../../../components/ClearingTable/Table';
import Button from '../../../components/Buttons/Button';
import AutoCompleteAccountNo from '../../../components/AutoComplete/AutoCompleteAccountNo';
import AutoCompleteSymbol from '../../../components/AutoComplete/AutoCompleteSymbol';
import SelectSystemCode from '../../../components/Dropdown/SelectSystemCode';
import Text from '../../../components/Typography/Text';
import WalletModal from '../../../components/Modals/WalletModal';
import { getCurrentUser, getAccess } from '../../../services/AuthService';
import { readClient, getAccessibleClient } from '../../../services/ClientAccountService';
import { protoTimeSpanObjectToString } from '../../../services/ConvertService';
import { formatPbDate } from '../../../lib/fmt';
import {
  notifyInfo,
  notifyError,
  notifySuccess,
} from '../../../components/Messages/Notification';
import { listWallet, createWallet, updateWallet } from '../../../services/WalletService';
import QueryParam from '../../../services/QueryParamService';
import {
  CustomBodyRenderAlign,
  CustomBodyRenderHtmlString,
} from '../../../components/Table/CustomBodyRender';

export default function Wallet() {
  const [lockTable, setLockTable] = useState(true);
  const [searchDataCsv, setSearchDataCsv] = React.useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [previousRows, setPreviousRows] = useState(0);
  const [pagination, setPagination] = useState({});
  const [rows, setRows] = React.useState([]);
  const [rowData, setRowData] = React.useState({});
  const [open, setOpen] = React.useState(false);

  const [loading, setLoading] = React.useState({
    search: false,
    save: false,
  });

  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      accountNo: '',
      masterAccountNo: '',
      correspondent: '',
      symbol: '',
      status: 'Active',
    })
  );

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    let account_no = '';
    let correspondent = '';
    let masterAccountNo = '';
    let symbol = 'AXX';
    try {
      const loggeduser = getCurrentUser();
      if (loggeduser.AccountId !== 0) {
        const account = await readClient(loggeduser.AccountId);
        account_no = account.client.accountNo;
        correspondent = account.client.correspondent;
        masterAccountNo = account.client.masterAccountNo;
      } else {
        const account = await getAccessibleClient(loggeduser.UserId);
        account_no = account.client.accountNo;
        correspondent = account.client.correspondent;
      }

      if (!getAccess('account_no', loggeduser.UserType)) {
        setSearchData({
          ...searchData,
          accountNo: account_no,
          correspondent: correspondent,
          masterAccountNo: masterAccountNo,
          symbol: symbol,
        });
      }
    } catch (error) {
      notifyError(error.message);
    }
  };

  const columns = [
    {
      name: 'actions',
      label: 'Actions',
      type: columnType.buttons,
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <div>
              <div style={{ padding: 5 }}>
                {tempFilteredRows[dataIndex]?.walletId !== 0 &&
                  ['Pending Approval', 'Active'].includes(
                    tempFilteredRows[dataIndex]?.status
                  ) && (
                    <IconButton
                      aria-label="edit"
                      onClick={() => handleOpen(tempFilteredRows[dataIndex])}
                    >
                      <EditIcon style={{ height: 24, width: 24 }} />
                    </IconButton>
                  )}
              </div>
            </div>
          );
        },
        sort: false,
      },
    },
    {
      name: 'symbol',
      label: 'Symbol',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'symbolDescription',
      label: 'Symbol Description',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'wallet',
      label: 'Wallet',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountNo',
      label: 'Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'note',
      label: 'Note',
      options: {
        customBodyRender: CustomBodyRenderHtmlString,
      },
      type: columnType.note,
    },
    {
      name: 'originalCusip',
      label: 'Original Cusip',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      // Already converted to string in handlesearch
      name: 'systemDate',
      label: 'System Date',
      type: columnType.date,
    },
    {
      name: 'createdBy',
      label: 'Created By',
      options: {
        display: false,
      },
    },
    {
      name: 'createdAt',
      label: 'Created At',
      options: {
        display: false,
        customBodyRenderLite: (dataIndex) => {
          return protoTimeSpanObjectToString(
            rows[dataIndex]?.createdAt,
            'MM/DD/YYYY hh:mm:ss a'
          );
        },
      },
    },
    {
      name: 'modifiedAt',
      label: 'Modified At',
      options: {
        display: false,
        customBodyRenderLite: (dataIndex) => {
          return protoTimeSpanObjectToString(
            rows[dataIndex]?.modifiedAt,
            'MM/DD/YYYY hh:mm:ss a'
          );
        },
      },
    },
    {
      name: 'modifiedBy',
      label: 'Modified By',
      options: {
        display: false,
      },
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    serverSide: true,
  };

  const handleSearchDataChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setSearchData({ ...searchData, [input.name]: input.value });
  };

  const getCsvData = async () => {
    if (lockTable === true) {
      return [];
    }
    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: totalRows,
    };

    const data = await listWallet(searchDataCsv, paginationCopy);

    const rowsCopy = data.walletsList.map((item) => {
      item.createdAt = item.createdAt
        ? protoTimeSpanObjectToString(item.createdAt, 'MM/DD/YYYY hh:mm:ss a')
        : '--';
      item.modifiedAt = item.modifiedAt
        ? protoTimeSpanObjectToString(item.modifiedAt, 'MM/DD/YYYY hh:mm:ss a')
        : '--';

      return item;
    });

    return rowsCopy;
  };

  const handleSearch = async (paginationChange) => {
    try {
      if (paginationChange !== true) {
        setLoading({ ...loading, search: true });
        setLockTable(false);
      } else {
        if (lockTable === true) {
          return;
        }
      }
      QueryParam.set(searchData);
      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };
      if (previousRows !== 0) {
        if (previousRows < pagination.rowsPerPage * pagination.pageNo) {
          paginationCopy.pageNo = 0;
        }
      }
      if (paginationChange !== true) {
        paginationCopy.filterNames = [];
        paginationCopy.filterValues = [];
      }
      const data = await listWallet(searchData, paginationCopy);
      setSearchDataCsv(searchData);
      setTotalRows(data.summary.totalRows);
      setPreviousRows(totalRows);
      paginationCopy.count = data.summary.totalRows;
      setPagination(paginationCopy);
      setRows(data.walletsList);
      if (paginationChange !== true) {
        notifyInfo(data.summary.totalRows + ' search results.');
      }
    } catch (error) {
      notifyError(error.message);
    } finally {
      setLoading({ ...loading, search: false });
    }
  };
  const handleOpen = (data) => {
    if (!data) {
      data = {
        walletId: '',
        accountId: '',
        symbol: '',
        accountNo: '',
        wallet: '',
        status: 'Pending Approval',
        note: '',
      };
    }

    setRowData(data);
    setOpen(true);
  };

  const handleClose = async (data, isEdit) => {
    if (!data) {
      setOpen(false);
      return;
    }

    let valid = true;

    if (!data.status) {
      notifyError('Status is required.');
      valid = false;
    }

    if (!data.wallet) {
      notifyError('Wallet is required.');
      valid = false;
    }

    if (!valid) {
      return;
    }

    try {
      setLoading({ ...loading, save: true });

      if (isEdit) {
        const resp = await updateWallet(data);
        const rowsCopy = [...rows];
        const index = rows.indexOf(rowData);

        rowsCopy[index] = resp.wallet;

        setRows(rowsCopy);
        notifySuccess('Wallet has been updated.');
      } else {
        const resp = await createWallet(data);

        setRows([resp.wallet, ...rows]);
        notifySuccess('New Wallet has been added.');
      }
    } catch (error) {
      setLoading({ ...loading, save: false });
      notifyError(error.message);
    } finally {
      setOpen(false);
      setLoading({ ...loading, save: false });
    }
  };

  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Wallet" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading.search}
              loading={loading.search}
              type="search"
              label={loading.search ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
          <div className="grd-cell-none">
            <Button type="plus" label="Add Wallet" onClick={() => handleOpen()} />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <AutoCompleteAccountNo
              name="accountNo"
              value={searchData.accountNo}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteSymbol
              name="symbol"
              value={searchData.symbol}
              onChange={handleSearchDataChange}
              assetType="DA"
            />
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              name="status"
              label="Status"
              placeholder="Status"
              type="Status"
              subType="Bank Account"
              value={searchData.status}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell"></div>
        </div>
      </div>

      <div className="mt-20">
        <Table
          title={'Wallet'}
          data={rows}
          columns={columns}
          options={options}
          onSearch={handleSearch}
          pagination={pagination}
          setPagination={setPagination}
          getCsvData={getCsvData}
        />
      </div>

      <WalletModal
        open={open}
        close={handleClose}
        data={rowData}
        loading={loading.save}
      />
    </form>
  );
}
